<script setup lang="ts">
type Props = {
  to?: string;
  isButton?: boolean;
  isActive?: boolean;
  target?: string;
  glowColor?: string;
};

const { to, isButton, isActive, target, glowColor } = defineProps<Props>();

const { closeMenu } = useLayoutStore();
const { isLayoutMobileOrTablet, isSidebarMenuOpen } = useLayout();

function onMenuClick() {
  if (isLayoutMobileOrTablet.value) {
    closeMenu();
  }
}

const glowStyle = computed(() =>
  glowColor
    ? ({ '--glow-color': glowColor } as Record<string, string>)
    : undefined,
);
</script>

<template>
  <button
    v-if="isButton"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :style="glowStyle"
    @click="onMenuClick"
  >
    <slot />
  </button>

  <NuxtLink
    v-else-if="to"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :external="target === '_blank'"
    no-prefetch
    :style="glowStyle"
    :target="target"
    :to="to"
    @click="onMenuClick"
  >
    <slot />
  </NuxtLink>
</template>

<style module lang="scss">
.menuLink {
  @apply flex justify-start items-center text-white text-sm transition-none font-semibold cursor-pointer relative overflow-hidden;
  background-color: #070f22;
  width: auto;
  height: 38px;
  padding: 0.864px;
  gap: 11.37px;
  border-radius: 11.37px;
  border-left: 2.8px solid #0606ad;
  padding-left: 15px;

  svg {
    @apply w-[20px] h-[20px] min-w-[20px] relative z-10;
  }

  img,
  [class*='sidebarIcon'] {
    @apply w-[20px] h-[20px] grayscale relative z-10;
  }

  span {
    @apply relative z-10;
  }

  &.isActive {
    @apply text-white opacity-100;
    border-left-color: #ff3400;

    img,
    [class*='sidebarIcon'] {
      @apply grayscale-0;
    }
  }

  @media (hover: hover) {
    &:hover {
      @apply text-white opacity-100;
      border-left-color: #ff3400;

      img,
      [class*='sidebarIcon'] {
        @apply grayscale-0;
      }
    }
  }

  &.hasGlow::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at -60% 80%,
      var(--glow-color) 0%,
      transparent 60%
    );
    pointer-events: none;
    z-index: 0;
  }

  &.hasGlow.isActive::before {
    --glow-color: #ff3700;
  }

  @media (hover: hover) {
    &.hasGlow:hover::before {
      --glow-color: #ff3700;
    }
  }

  @media screen and (min-width: 1024px) {
    &.isClosed {
      @apply w-[48px] h-[48px] flex justify-center items-center mb-1.5 mx-auto bg-[#FDF8E9] invert rounded-[6px] px-0;
      background-color: #fdf8e9;
      border: 1.184px solid rgba(0, 0, 0, 0.1);

      &::before {
        display: none;
      }

      span:not([class*='sidebarIcon']):not([class*='spinner']) {
        @apply hidden;
      }

      svg,
      img {
        @apply text-xl invert text-white;
      }

      [class*='sidebarIcon'] {
        @apply opacity-100;
      }

      &.isActive {
        background: rgba(247, 169, 105, 1);
      }
    }
  }
}
</style>
